import { useState } from 'react'
import { IoChevronDown, IoChevronUp, IoAdd, IoCheckmark } from 'react-icons/io5'
import type { PlayerSOTItem } from '../../types'
import { COUNTRY_FLAGS } from '../../context/LeagueContext'

interface PlayerStatsTableProps {
  items: PlayerSOTItem[]
  sortBy: string
  sortOrder: 'asc' | 'desc'
  onSortChange: (column: string) => void
}

const COLUMNS = [
  { key: 'league', label: 'League', sortable: false, width: '70px', tooltip: 'Competition the match is in' },
  { key: 'fixture', label: 'Fixture', sortable: false, width: '180px', tooltip: 'Home team vs Away team' },
  { key: 'kickoff', label: 'Kickoff', sortable: true, sortKey: 'kickoff', width: '90px', tooltip: 'Match kickoff time in your local timezone' },
  { key: 'player', label: 'Player', sortable: false, width: '140px', tooltip: 'Player name' },
  { key: 'position', label: 'Pos', sortable: false, width: '50px', tooltip: 'Player position: FWD (Forward), MID (Midfielder), DEF (Defender), GK (Goalkeeper)' },
  { key: 'last5_avg', label: 'Avg SOT', sortable: true, sortKey: 'last5_avg', width: '70px', tooltip: 'Average shots on target per game over last 5 matches' },
  { key: 'expected_mins', label: 'Exp Mins', sortable: true, sortKey: 'expected_mins', width: '80px', tooltip: 'Expected minutes to play based on recent starts, rotation patterns, and availability' },
  { key: 'position_factor', label: 'Pos x', sortable: false, width: '55px', tooltip: 'Position multiplier - how much a player\'s position affects SOT likelihood (>1 = more likely, <1 = less likely)' },
  { key: 'opp_sot_conceded', label: 'Opp SOT', sortable: false, width: '70px', tooltip: 'Opponent\'s average shots on target conceded per game - higher = weaker defense' },
  { key: 'probability', label: 'Prob', sortable: true, sortKey: 'probability', width: '70px', tooltip: 'Model confidence in 1+ SOT. Green (60%+) = High, Yellow (40-59%) = Medium, Red (<40%) = Low' },
  { key: 'action', label: '', sortable: false, width: '80px', tooltip: '' },
]

function formatKickoff(isoDate: string): string {
  const date = new Date(isoDate)
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  const day = days[date.getDay()]
  const hours = date.getHours().toString().padStart(2, '0')
  const mins = date.getMinutes().toString().padStart(2, '0')
  return `${day} ${hours}:${mins}`
}

function getPositionClass(position: string | null): string {
  if (!position) return ''
  const pos = position.toLowerCase()
  if (['attacker', 'forward', 'striker', 'f', 'st', 'cf', 'lw', 'rw'].includes(pos)) return 'attacker'
  if (['midfielder', 'midfield', 'm', 'cm', 'dm', 'am', 'lm', 'rm', 'cdm', 'cam'].includes(pos)) return 'midfielder'
  if (['defender', 'defence', 'd', 'cb', 'lb', 'rb', 'lwb', 'rwb'].includes(pos)) return 'defender'
  return ''
}

function getPositionLabel(position: string | null): string {
  if (!position) return '-'
  const pos = position.toLowerCase()
  if (['attacker', 'forward', 'striker', 'f', 'st', 'cf', 'lw', 'rw'].includes(pos)) return 'FWD'
  if (['midfielder', 'midfield', 'm', 'cm', 'dm', 'am', 'lm', 'rm', 'cdm', 'cam'].includes(pos)) return 'MID'
  if (['defender', 'defence', 'd', 'cb', 'lb', 'rb', 'lwb', 'rwb'].includes(pos)) return 'DEF'
  if (['goalkeeper', 'g', 'gk'].includes(pos)) return 'GK'
  return pos.substring(0, 3).toUpperCase()
}

function getProbabilityClass(prob: number): string {
  if (prob >= 60) return 'prob-high'
  if (prob >= 40) return 'prob-med'
  return 'prob-low'
}

function getProbabilityIndicator(prob: number): string {
  if (prob >= 60) return 'H'
  if (prob >= 40) return 'M'
  return 'L'
}

function getCountryFlag(country: string | null): string {
  if (!country) return ''
  return COUNTRY_FLAGS[country] || ''
}

export function PlayerStatsTable({ items, sortBy, sortOrder, onSortChange }: PlayerStatsTableProps) {
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [addedPlayers, setAddedPlayers] = useState<Set<number>>(new Set())

  const handleRowClick = (playerId: number) => {
    setExpandedId(expandedId === playerId ? null : playerId)
  }

  const handleAddToBuilder = (e: React.MouseEvent, item: PlayerSOTItem) => {
    e.stopPropagation()
    // TODO: Integrate with bet builder context
    setAddedPlayers((prev) => new Set(prev).add(item.player_id))
  }

  const renderSortIcon = (column: typeof COLUMNS[0]) => {
    if (!column.sortable || !column.sortKey) return null
    if (sortBy !== column.sortKey) {
      return <span className="sort-icon sort-icon-inactive">{'\u2195'}</span>
    }
    return (
      <span className="sort-icon">
        {sortOrder === 'desc' ? '\u2193' : '\u2191'}
      </span>
    )
  }

  return (
    <div className="player-stats-table-container">
      <table className="player-stats-table">
        <thead>
          <tr>
            {COLUMNS.map((col) => (
              <th
                key={col.key}
                style={{ width: col.width }}
                className={col.sortable ? 'sortable' : ''}
                onClick={() => col.sortable && col.sortKey && onSortChange(col.sortKey)}
                title={col.tooltip}
              >
                {col.label}
                {renderSortIcon(col)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <>
              <tr
                key={item.player_id}
                onClick={() => handleRowClick(item.player_id)}
                className={expandedId === item.player_id ? 'expanded' : ''}
              >
                <td>
                  <span title={item.league_name}>
                    {getCountryFlag(item.league_country)}
                  </span>
                </td>
                <td className="fixture-cell">
                  <span className="fixture-label" title={item.fixture_label}>
                    {item.fixture_label}
                  </span>
                </td>
                <td>{formatKickoff(item.kickoff_utc)}</td>
                <td className="player-cell">
                  <span className="player-name">{item.player_name}</span>
                </td>
                <td>
                  <span className={`position-badge ${getPositionClass(item.position)}`}>
                    {getPositionLabel(item.position)}
                  </span>
                </td>
                <td>{item.last5_avg.toFixed(1)}</td>
                <td>{item.expected_minutes != null ? Math.round(item.expected_minutes) : '-'}</td>
                <td>{item.position_factor.toFixed(2)}</td>
                <td>{item.opp_sot_conceded.toFixed(1)}</td>
                <td>
                  <span className={`prob-badge ${getProbabilityClass(item.conviction_pct)}`}>
                    {item.conviction_pct}%
                    <span className="prob-indicator">{getProbabilityIndicator(item.conviction_pct)}</span>
                  </span>
                </td>
                <td>
                  <button
                    className={`player-stats-add-btn ${addedPlayers.has(item.player_id) ? 'added' : ''}`}
                    onClick={(e) => handleAddToBuilder(e, item)}
                    disabled={addedPlayers.has(item.player_id)}
                  >
                    {addedPlayers.has(item.player_id) ? (
                      <>
                        <IoCheckmark size={14} /> Added
                      </>
                    ) : (
                      <>
                        <IoAdd size={14} /> Add
                      </>
                    )}
                  </button>
                </td>
              </tr>
              {expandedId === item.player_id && (
                <tr className="player-stats-expanded-row">
                  <td colSpan={COLUMNS.length}>
                    <div className="player-stats-expanded-content">
                      <div className="player-stats-expanded-stat">
                        <span className="label">Season Avg</span>
                        <span className="value">{item.season_avg != null ? item.season_avg.toFixed(2) : '-'}</span>
                      </div>
                      <div className="player-stats-expanded-stat">
                        <span className="label">Start Prob</span>
                        <span className="value">{item.start_probability != null ? `${Math.round(item.start_probability * 100)}%` : '-'}</span>
                      </div>
                      <div className="player-stats-expanded-stat">
                        <span className="label">Role</span>
                        <span className="value">{item.role || '-'}</span>
                      </div>
                      <div className="player-stats-expanded-stat">
                        <span className="label">SOT/Start</span>
                        <span className="value">{item.sot_per_start != null ? item.sot_per_start.toFixed(2) : '-'}</span>
                      </div>
                      <div className="player-stats-expanded-stat">
                        <span className="label">SOT/Sub</span>
                        <span className="value">{item.sot_per_sub != null ? item.sot_per_sub.toFixed(2) : '-'}</span>
                      </div>
                      <div className="player-stats-expanded-stat">
                        <span className="label">Primary Line</span>
                        <span className="value">{item.primary_line != null ? item.primary_line : '-'}</span>
                      </div>
                      <div className="player-stats-expanded-why">
                        <strong>Analysis:</strong> {item.why || 'No analysis available'}
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </>
          ))}
        </tbody>
      </table>
    </div>
  )
}
